import { takeEvery, put, all } from 'redux-saga/effects';
import { toBoolean } from 'underscore.string';

import * as types from '../../actions/candidate/candidate-profile/types';
import * as actions from '../../actions/candidate/candidate-profile';

import { countryWithStateIds } from '../../constants/country-ids';

import { loaderDecorator } from '../decorators/loader-decorator';

import { checkMaritalStatusIncludesPartner } from '../../tools';
import apiFetcher from '../../tools/api-fetcher';
import formDataEncode from '../../tools/form-data-encode';
import handleError from '../../tools/error-helper';

function* fetchCandidateProfileSectionsStates() {
  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`candidates/current/profile/state`);
    yield put(actions.setCandidateProfileSectionsStates(data));
  } catch (exception) {
    handleError(exception);
  }
}

function* fetchCandidateProfileBiographicalInformation() {
  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`candidates/current/profile/biographical-information`);
    yield put(actions.setCandidateProfileBiographicalInformation(data));
  } catch (exception) {
    handleError(exception);
  }
}

function* submitCandidateProfileBiographicalInformation({
  biographicalInformation,
  onUpdated
}) {
  try {
    yield apiFetcher
      .auth()
      .put(
        `candidates/current/profile/biographical-information`,
        biographicalInformation
      );
    onUpdated();
    yield fetchCandidateProfileBiographicalInformation();
  } catch (exception) {
    handleError(exception);
  }
}

function* fetchCandidateProfileHousingPreferences() {
  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`candidates/current/profile/housing-preferences`);
    yield put(actions.setCandidateProfileHousingPreferences(data));
  } catch (exception) {
    handleError(exception);
  }
}

function* submitCandidateProfileHousingPreferences({
  housingPreferences,
  onUpdated
}) {
  try {
    yield apiFetcher
      .auth()
      .put(
        `candidates/current/profile/housing-preferences`,
        housingPreferences
      );
    onUpdated();
    yield fetchCandidateProfileHousingPreferences();
  } catch (exception) {
    handleError(exception);
  }
}

function* fetchCandidateProfileHealth() {
  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`candidates/current/profile/health`);
    yield put(actions.setCandidateProfileHealth(data));
  } catch (exception) {
    handleError(exception);
  }
}

function* submitCandidateProfileHealth({ health, onUpdated }) {
  try {
    yield apiFetcher.auth().put(`candidates/current/profile/health`, health);
    onUpdated();
    yield fetchCandidateProfileHealth();
  } catch (exception) {
    handleError(exception);
  }
}

function* fetchCandidateProfileSportActivities() {
  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`candidates/current/profile/sports-and-activities`);
    yield put(actions.setCandidateProfileSportActivities(data));
  } catch (exception) {
    handleError(exception);
  }
}

function* submitCandidateProfileSportActivities({
  sportActivities,
  onUpdated
}) {
  try {
    yield apiFetcher
      .auth()
      .put(`candidates/current/profile/sports-and-activities`, sportActivities);
    onUpdated();
    yield fetchCandidateProfileSportActivities();
  } catch (exception) {
    handleError(exception);
  }
}

function* fetchCandidateProfileLanguages() {
  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`candidates/current/profile/languages`);
    yield put(actions.setCandidateProfileLanguages(data));
  } catch (exception) {
    handleError(exception);
  }
}

function* submitCandidateProfileLanguages({ languages, onUpdated }) {
  try {
    yield apiFetcher
      .auth()
      .put(`candidates/current/profile/languages`, languages);
    onUpdated();
    yield fetchCandidateProfileLanguages();
  } catch (exception) {
    handleError(exception);
  }
}

function* fetchCandidateProfilePersonal() {
  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`candidates/current/profile/personal`);
    yield put(actions.setCandidateProfilePersonal(data));
  } catch (exception) {
    handleError(exception);
  }
}

function* submitCandidateProfilePersonal({ personal, onUpdated }) {
  try {
    if (!personal.partnerIsTeacher) {
      personal.firstPartnerPositionId = undefined;
      personal.secondPartnerPositionId = undefined;
      personal.thirdPartnerPositionId = undefined;
      personal.partnerIsCandidate = undefined;
    }

    if (!checkMaritalStatusIncludesPartner(personal.maritalStatus)) {
      personal.partnerFirstName = undefined;
      personal.partnerLastName = undefined;
      personal.partnerMiddleName = undefined;
      personal.partnerIsTeacher = undefined;
    }

    if (!toBoolean(personal.partnerIsCandidate)) {
      personal.partnerCandidateAccountEmail = undefined;
    }

    yield apiFetcher
      .auth()
      .put(`candidates/current/profile/personal`, formDataEncode(personal));
    onUpdated();
    yield fetchCandidateProfilePersonal();
  } catch (exception) {
    handleError(exception);
  }
}

function* fetchCandidateProfileSecondarySchools() {
  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`candidates/current/profile/secondary-schools`);
    yield put(actions.setCandidateProfileSecondarySchools(data));
  } catch (exception) {
    handleError(exception);
  }
}

function* submitCandidateProfileSecondarySchools({
  secondarySchools,
  onUpdated
}) {
  try {
    yield apiFetcher
      .auth()
      .put(`candidates/current/profile/secondary-schools`, secondarySchools);
    onUpdated();
    yield fetchCandidateProfileSecondarySchools();
  } catch (exception) {
    handleError(exception);
  }
}

function* fetchCandidateProfileUniversitiesColleges() {
  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`candidates/current/profile/higher-educations`);
    yield put(actions.setCandidateProfileUniversitiesColleges(data));
  } catch (exception) {
    handleError(exception);
  }
}

function* submitCandidateProfileUniversitiesColleges({
  universitiesColleges,
  onUpdated
}) {
  try {
    yield apiFetcher
      .auth()
      .put(
        `candidates/current/profile/higher-educations`,
        universitiesColleges
      );
    onUpdated();
    yield fetchCandidateProfileUniversitiesColleges();
  } catch (exception) {
    handleError(exception);
  }
}

function* fetchCandidateProfileTeachingExperience() {
  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`candidates/current/profile/teaching-experience`);
    yield put(actions.setCandidateProfileTeachingExperience(data));
  } catch (exception) {
    handleError(exception);
  }
}

function* submitCandidateProfileTeachingExperience({
  teachingExperience,
  onUpdated
}) {
  try {
    if (!toBoolean(teachingExperience.hasWorkHistoryGaps)) {
      teachingExperience.workHistoryGaps = undefined;
    }

    for (var index in teachingExperience.experiences) {
      var experience = teachingExperience.experiences[index];

      if (experience.address) {
        if (countryWithStateIds.includes(+experience.address.countryId)) {
          experience.address.state = undefined;
        } else {
          experience.address.stateId = undefined;
          experience.address.zipCode = undefined;
        }
      }

      if (experience.isStillInThisPosition) {
        experience.reasonForLeaving = undefined;
        experience.endDate = undefined;
      }

      for (var positionIndex in experience.positions) {
        var position = experience.positions[positionIndex];

        if (position.hasNoGradeLevels) {
          position.gradeLevelIds = [];
        }
      }
    }

    yield apiFetcher
      .auth()
      .put(
        `candidates/current/profile/teaching-experience`,
        teachingExperience
      );
    onUpdated();
    yield fetchCandidateProfileTeachingExperience();
  } catch (exception) {
    handleError(exception);
  }
}

function* fetchCandidateProfileContactInformation() {
  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`candidates/current/profile/contact-information`);
    yield put(actions.setCandidateProfileContactInformation(data));
  } catch (exception) {
    handleError(exception);
  }
}

function* submitCandidateProfileContactInformation({
  contactInformation,
  onUpdated
}) {
  try {
    if (
      !toBoolean(contactInformation.additionalAddresses.hasAdditionalAddresses)
    ) {
      contactInformation.additionalAddresses.additionalAddressList = [];
    }

    if (countryWithStateIds.includes(+contactInformation.address.countryId)) {
      contactInformation.address.state = undefined;
    } else {
      contactInformation.address.stateId = undefined;
      contactInformation.address.zipCode = undefined;
    }

    for (var index in contactInformation.additionalAddresses
      .additionalAddressList) {
      if (
        contactInformation.additionalAddresses.additionalAddressList[index]
          .address
      ) {
        if (
          countryWithStateIds.includes(
            +contactInformation.additionalAddresses.additionalAddressList[index]
              .address.countryId
          )
        ) {
          contactInformation.additionalAddresses.additionalAddressList[
            index
          ].address.state = undefined;
        } else {
          contactInformation.additionalAddresses.additionalAddressList[
            index
          ].address.stateId = undefined;
          contactInformation.additionalAddresses.additionalAddressList[
            index
          ].address.zipCode = undefined;
        }
      }
    }

    yield apiFetcher
      .auth()
      .put(
        `candidates/current/profile/contact-information`,
        contactInformation
      );
    onUpdated();
    yield fetchCandidateProfileContactInformation();
  } catch (exception) {
    handleError(exception);
  }
}

function* fetchCandidateProfileTeachingCredentials() {
  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`candidates/current/profile/teaching-credentials`);

    yield put(actions.setCandidateProfileTeachingCredentials(data));
  } catch (exception) {
    handleError(exception);
  }
}

function* submitCandidateProfileTeachingCredentials({
  teachingCredentials,
  onUpdated
}) {
  try {
    yield apiFetcher
      .auth()
      .put(
        `candidates/current/profile/teaching-credentials`,
        teachingCredentials
      );
    onUpdated();
    yield fetchCandidateProfileTeachingCredentials();
  } catch (exception) {
    handleError(exception);
  }
}

function* getAllCandidateProfileSections() {
  yield put(actions.setCandidateProfileLoadingLoader(true));

  yield fetchCandidateProfileBiographicalInformation();
  yield fetchCandidateProfileContactInformation();
  yield fetchCandidateProfileHealth();
  yield fetchCandidateProfileHousingPreferences();
  yield fetchCandidateProfileLanguages();
  yield fetchCandidateProfilePersonal();
  yield fetchCandidateProfileSecondarySchools();
  yield fetchCandidateProfileSportActivities();
  yield fetchCandidateProfileTeachingCredentials();
  yield fetchCandidateProfileTeachingExperience();
  yield fetchCandidateProfileUniversitiesColleges();

  yield put(actions.setCandidateProfileLoadingLoader(false));
}

export default function* candidateProfileSaga() {
  yield all([
    takeEvery(
      types.CANDIDATE_PROFILE_FETCH_PROFILE_SECTIONS_STATES,
      loaderDecorator(
        fetchCandidateProfileSectionsStates,
        actions.setCandidateProfileSectionsStatesLoadingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_FETCH_BIOGRAPHICAL_INFORMATION,
      loaderDecorator(
        fetchCandidateProfileBiographicalInformation,
        actions.setCandidateProfileLoadingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_SUBMIT_BIOGRAPHICAL_INFORMATION,
      loaderDecorator(
        submitCandidateProfileBiographicalInformation,
        actions.setCandidateProfileSubmittingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_FETCH_HOUSING_PREFERENCES,
      loaderDecorator(
        fetchCandidateProfileHousingPreferences,
        actions.setCandidateProfileLoadingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_SUBMIT_HOUSING_PREFERENCES,
      loaderDecorator(
        submitCandidateProfileHousingPreferences,
        actions.setCandidateProfileSubmittingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_FETCH_HEALTH,
      loaderDecorator(
        fetchCandidateProfileHealth,
        actions.setCandidateProfileLoadingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_SUBMIT_HEALTH,
      loaderDecorator(
        submitCandidateProfileHealth,
        actions.setCandidateProfileSubmittingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_FETCH_SPORT_ACTIVITIES,
      loaderDecorator(
        fetchCandidateProfileSportActivities,
        actions.setCandidateProfileLoadingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_SUBMIT_SPORT_ACTIVITIES,
      loaderDecorator(
        submitCandidateProfileSportActivities,
        actions.setCandidateProfileSubmittingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_FETCH_LANGUAGES,
      loaderDecorator(
        fetchCandidateProfileLanguages,
        actions.setCandidateProfileLoadingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_SUBMIT_LANGUAGES,
      loaderDecorator(
        submitCandidateProfileLanguages,
        actions.setCandidateProfileSubmittingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_FETCH_PERSONAL,
      loaderDecorator(
        fetchCandidateProfilePersonal,
        actions.setCandidateProfileLoadingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_SUBMIT_PERSONAL,
      loaderDecorator(
        submitCandidateProfilePersonal,
        actions.setCandidateProfileSubmittingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_FETCH_SECONDARY_SCHOOLS,
      loaderDecorator(
        fetchCandidateProfileSecondarySchools,
        actions.setCandidateProfileLoadingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_SUBMIT_SECONDARY_SCHOOLS,
      loaderDecorator(
        submitCandidateProfileSecondarySchools,
        actions.setCandidateProfileSubmittingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_FETCH_UNIVERSITIES_COLLEGES,
      loaderDecorator(
        fetchCandidateProfileUniversitiesColleges,
        actions.setCandidateProfileLoadingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_SUBMIT_UNIVERSITIES_COLLEGES,
      loaderDecorator(
        submitCandidateProfileUniversitiesColleges,
        actions.setCandidateProfileSubmittingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_FETCH_TEACHING_EXPERIENCE,
      loaderDecorator(
        fetchCandidateProfileTeachingExperience,
        actions.setCandidateProfileLoadingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_SUBMIT_TEACHING_EXPERIENCE,
      loaderDecorator(
        submitCandidateProfileTeachingExperience,
        actions.setCandidateProfileSubmittingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_FETCH_CONTACT_INFORMATION,
      loaderDecorator(
        fetchCandidateProfileContactInformation,
        actions.setCandidateProfileLoadingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_SUBMIT_CONTACT_INFORMATION,
      loaderDecorator(
        submitCandidateProfileContactInformation,
        actions.setCandidateProfileSubmittingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_FETCH_TEACHING_CREDENTIALS,
      loaderDecorator(
        fetchCandidateProfileTeachingCredentials,
        actions.setCandidateProfileLoadingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_SUBMIT_TEACHING_CREDENTIALS,
      loaderDecorator(
        submitCandidateProfileTeachingCredentials,
        actions.setCandidateProfileSubmittingLoader
      )
    ),
    takeEvery(
      types.CANDIDATE_PROFILE_GET_ALL_SECTIONS,
      getAllCandidateProfileSections
    )
  ]);
}



// WEBPACK FOOTER //
// ./src/sagas/candidate/candidate-profile.js